import { useCallback, useEffect, useState } from 'react'
import { CLOUD_API } from '@core/pages/admin/shared'
import { useAuthToken } from '@core/pages/admin/useAuthToken'
import { httpErrorText } from '@core/utils/httpError'
import { btn, btnBase, btnSize, cx, fieldInput, fieldLabel, hint } from '@ui/classes'
import { errBanner, okBanner, sectionCard } from '@ui/admin'

interface Credentials {
  configured: boolean
  inn?: string
  thumbprint_tail?: string | null
  has_token?: boolean
  status?: string
  last_error?: string | null
  checked_at?: string | null
  updated_at?: string
  token_expires_at?: string | null
  token_expires_in_seconds?: number | null
  token_expired?: boolean
  needs_refresh?: boolean
}

function remainingText(seconds: number | null | undefined): string {
  if (seconds == null) return 'срок неизвестен'
  if (seconds <= 0) return 'истёк'
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  if (hours > 0) return `осталось ${hours} ч ${minutes} мин`
  return `осталось ${Math.max(1, minutes)} мин`
}

const STATUS_LABELS: Record<string, string> = {
  not_configured: 'Не настроено',
  pending: 'Ожидает проверки',
  active: 'Активно',
  error: 'Ошибка',
  disabled: 'Отключено'
}

/** Должно совпадать с backend TOKEN_REFRESH_WINDOW_SECONDS. */
const TOKEN_REFRESH_WINDOW_SECONDS = 2 * 60 * 60

/** 65.13: креды ЧЗ организации. Секреты только записываются, но не читаются. */
export function CzCredentialsSection({ canEdit }: { canEdit: boolean }) {
  const token = useAuthToken()
  const [data, setData] = useState<Credentials | null>(null)
  const [inn, setInn] = useState('')
  const [thumbprint, setThumbprint] = useState('')
  const [secret, setSecret] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())

  const load = useCallback(async () => {
    // GET требует admin; кассиру карточка доступна read-only без запроса.
    if (!token || !canEdit) {
      setData(null)
      return
    }
    setError(null)
    try {
      const response = await fetch(`${CLOUD_API}/tenant/cz/credentials`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        setError(httpErrorText('Доступ к Честному Знаку', response.status))
        return
      }
      const next = (await response.json()) as Credentials
      setData(next)
      setInn(next.inn ?? '')
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [token, canEdit])

  useEffect(() => {
    void load()
  }, [load])

  // Срок жизни токена должен «тикать» без перезагрузки страницы: иначе
  // предупреждение за 2 часа не появится у открытой админки.
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 30_000)
    return () => window.clearInterval(timer)
  }, [])

  const save = async (checkAfter = false) => {
    if (!token || !canEdit) return
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const body: Record<string, string> = { inn: inn.trim() }
      if (thumbprint.trim()) body.thumbprint = thumbprint.trim()
      if (secret.trim()) body.token = secret.trim()
      const response = await fetch(`${CLOUD_API}/tenant/cz/credentials`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(body)
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null
        setError(payload?.error ?? httpErrorText('Сохранение кредов', response.status))
        return
      }
      const next = (await response.json()) as Credentials
      setData(next)
      setSecret('')
      setThumbprint('')
      setNote('Креды сохранены. Токен повторно не отображается.')
      if (checkAfter) {
        await check()
      } else {
        await load()
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const check = async () => {
    if (!token || !canEdit) return
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const response = await fetch(`${CLOUD_API}/tenant/cz/credentials/check`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        const payload = (await response.json().catch(() => null)) as { error?: string } | null
        setError(payload?.error ?? httpErrorText('Проверка кредов', response.status))
        await load()
        return
      }
      setNote('Креды проверены')
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const disable = async () => {
    if (!token || !canEdit || !data?.configured) return
    if (!window.confirm('Отключить интеграцию Честного Знака для организации?')) return
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      const response = await fetch(`${CLOUD_API}/tenant/cz/credentials`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      if (!response.ok) {
        setError(httpErrorText('Отключение кредов', response.status))
        return
      }
      setSecret('')
      setThumbprint('')
      setNote('Интеграция отключена')
      await load()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const status = data?.status ?? 'not_configured'
  const expiresAtMs = data?.token_expires_at
    ? new Date(data.token_expires_at).getTime()
    : null
  const expiresInSeconds =
    expiresAtMs != null && Number.isFinite(expiresAtMs)
      ? Math.floor((expiresAtMs - nowMs) / 1000)
      : (data?.token_expires_in_seconds ?? null)
  const expired =
    expiresAtMs != null && Number.isFinite(expiresAtMs)
      ? expiresAtMs <= nowMs
      : Boolean(data?.token_expired)
  const needsRefresh =
    Boolean(data?.has_token) &&
    (expiresInSeconds != null
      ? expiresInSeconds <= TOKEN_REFRESH_WINDOW_SECONDS
      : Boolean(data?.needs_refresh))
  const badgeLabel = expired ? 'Токен истёк' : (STATUS_LABELS[status] ?? status)

  return (
    <div className={sectionCard}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="m-0 text-big font-bold">Доступ к Честному Знаку</h3>
        <span
          className={cx(
            'rounded px-2 py-1 text-tiny font-semibold',
            expired || status === 'error'
              ? 'bg-red-100 text-dangerDark'
              : status === 'active'
                ? 'bg-green-100 text-green-800'
                : needsRefresh
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-soft text-muted'
          )}
        >
          {badgeLabel}
        </span>
      </div>
      <p className={hint}>
        Креды привязаны к организации. Токен хранится в зашифрованном виде и
        никогда не возвращается в браузер.
      </p>
      {data?.token_expires_at && (
        <p
          className={cx(
            hint,
            expired ? 'font-semibold text-dangerDark' : needsRefresh ? 'text-amber-800' : ''
          )}
        >
          Токен действует до {new Date(data.token_expires_at).toLocaleString('ru-RU')} ·{' '}
          {remainingText(expiresInSeconds)}
        </p>
      )}
      {expired ? (
        <p className={errBanner}>
          Срок действия токена истёк. Обновите токен и нажмите «Сохранить и проверить».
        </p>
      ) : (
        needsRefresh && (
          <p className={errBanner}>
            Токен истекает менее чем через 2 часа. Сохраните новый токен до начала смены.
          </p>
        )
      )}
      {error && <p className={errBanner}>{error}</p>}
      {note && <p className={okBanner}>{note}</p>}
      {!token ? (
        <p className={hint}>Подключите облако, чтобы настроить доступ к ЧЗ.</p>
      ) : !canEdit ? (
        <p className={hint}>Изменять креды может только администратор.</p>
      ) : (
        <>
          <div className="grid gap-2 md:grid-cols-2">
            <label className={fieldLabel}>
              ИНН организации
              <input
                className={fieldInput}
                value={inn}
                onChange={(e) => setInn(e.target.value)}
                placeholder="10 или 12 цифр"
                disabled={busy}
              />
            </label>
            <label className={fieldLabel}>
              Отпечаток сертификата
              <input
                className={fieldInput}
                value={thumbprint}
                onChange={(e) => setThumbprint(e.target.value)}
                placeholder={data?.thumbprint_tail ?? '40 или 64 hex-символа'}
                disabled={busy}
              />
            </label>
          </div>
          <label className={cx(fieldLabel, 'mt-2')}>
            Токен ЧЗ
            <input
              className={fieldInput}
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={data?.has_token ? 'Токен уже задан; оставьте пустым' : 'Вставьте токен'}
              disabled={busy}
              autoComplete="new-password"
            />
          </label>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={cx(btnBase, btn.primary, btnSize.sm)}
              disabled={busy}
              onClick={() => void save(true)}
            >
              {busy ? 'Проверяем…' : 'Сохранить и проверить'}
            </button>
            <button
              type="button"
              className={cx(btnBase, btn.secondary, btnSize.sm)}
              disabled={busy || !data?.configured || data.status === 'disabled'}
              onClick={() => void check()}
            >
              Проверить
            </button>
            <button
              type="button"
              className={cx(btnBase, btn.softDanger, btnSize.sm)}
              disabled={busy || !data?.configured || data.status === 'disabled'}
              onClick={() => void disable()}
            >
              Отключить
            </button>
          </div>
          {data?.last_error && <p className={errBanner}>{data.last_error}</p>}
          {data?.checked_at && (
            <p className={hint}>
              Проверено: {new Date(data.checked_at).toLocaleString('ru-RU')}
            </p>
          )}
        </>
      )}
    </div>
  )
}

